import { Patient, ReportData } from "../types/types";

interface DashboardStats {
  todayReports: number;
  pendingTests: number;
  totalPatients: number;
  todayRevenue: number;
}

import { Test } from "../types/types";

interface FormattedReport {
  id: string;
  patient: string;
  type: string;
  status: string;
  time: string;
  date: string;
  patientInfo?: Patient;
  bloodTests?: Test[];  // Added for showing test records
  urineTests?: Test[];  // Added for showing test records
}

export const calculateDashboardStats = (reports: ReportData[]): DashboardStats => {
  const today = new Date().toDateString();

  const todayReports = reports.filter(report => {
    let reportDate: Date;
    if (report.createdAt instanceof Date) {
      reportDate = report.createdAt;
    } else if (typeof report.createdAt === 'number') {
      reportDate = new Date(report.createdAt);
    } else if (report.createdAt && typeof (report.createdAt as { toDate?: () => Date }).toDate === 'function') {
      reportDate = (report.createdAt as { toDate: () => Date }).toDate();
    } else if (typeof report.createdAt === 'string') {
      reportDate = new Date(report.createdAt);
    } else {
      reportDate = new Date();
    }
    return reportDate.toDateString() === today;
  });

  const uniquePatients = new Set(reports.map(report => report.patientInfo?.patientId)).size;

  // Calculate revenue based on report type or individual test prices
  const todayRevenue = todayReports.reduce((total, report) => {
    // If report has individual tests with prices, sum them up
    let reportTotal = 0;

    // Check blood tests
    if (report.bloodTests && report.bloodTests.length > 0) {
      const bloodTestsRevenue = report.bloodTests.reduce((sum, test) => {
        // Only count price if test has a value (was performed) and has a price
        if (test.value && test.value.trim() !== '' && (test as Test & { price?: string | number }).price) {
          return sum + (Number((test as Test & { price?: string | number }).price) || 0);
        }
        return sum;
      }, 0);
      reportTotal += bloodTestsRevenue;
    }

    // Check urine tests
    if (report.urineTests && report.urineTests.length > 0) {
      const urineTestsRevenue = report.urineTests.reduce((sum, test) => {
        if (test.value && test.value.trim() !== '' && (test as Test & { price?: string | number }).price) {
          return sum + (Number((test as Test & { price?: string | number }).price) || 0);
        }
        return sum;
      }, 0);
      reportTotal += urineTestsRevenue;
    }

    // If no specific test prices found, fallback to default report prices
    if (reportTotal === 0) {
      reportTotal = report.type === 'Urine Report' || report.type === 'Urine Analysis' ? 300 : 500;
    }

    return total + reportTotal;
  }, 0);

  // Calculate pending tests
  const pendingTests = reports.filter(report => report.status === 'pending').length;

  return {
    todayReports: todayReports.length,
    pendingTests: pendingTests,
    totalPatients: uniquePatients,
    todayRevenue: todayRevenue
  };
};

export const formatReportsForDisplay = (reports: ReportData[], limit: number = 10): FormattedReport[] => {
  return reports.slice(0, limit).map(report => {
    let reportDate: Date;

    if (report.createdAt instanceof Date) {
      reportDate = report.createdAt;
    } else if (typeof report.createdAt === 'number') {
      reportDate = new Date(report.createdAt);
    } else if (report.createdAt && typeof (report.createdAt as { toDate?: () => Date }).toDate === 'function') {
      reportDate = (report.createdAt as { toDate: () => Date }).toDate();
    } else if (typeof report.createdAt === 'string') {
      reportDate = new Date(report.createdAt);
    } else {
      reportDate = new Date();
    }

    return {
      id: report.id || `TEST${Date.now().toString().slice(-3)}`,
      patient: report.patientInfo?.name || "Unknown Patient",
      patientInfo: report.patientInfo, // Added patientInfo
      type: report.type || "Blood Report",
      status: report.status || "Completed",
      time: reportDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: reportDate.toLocaleDateString(),
      bloodTests: report.bloodTests,
      urineTests: report.urineTests
    };
  });
};

export const createInitialPatientInfo = (): Patient => ({
  id: Date.now().toString(), // Generate a unique ID
  name: "",
  age: "",
  gender: "",
  patientId: "",
  doctorName: "",
  salutation: "", // Add salutation as it's required by Patient interface
  testDate: "",
  reportDate: new Date().toISOString().split("T")[0],
  email: "",
  phone: ""
});